import "./documents.scss";
import { useContext, useEffect, useState } from "react";

import Spinner from "../../../../shared/components/spinner/spinner";
import AuthContext from "../../../../shared/context/auth-context";
import useHttp from "../../../../shared/hooks/use-http";
import { useHttpNonTable } from "../../../../shared/hooks/use-httpNonTable";
import { deleteMultiple,downloadFile,getAllData, postSingleNonTableImport } from "../../../../shared/utils/api";
import { validateLoadingStatusNew } from "../../../../shared/utils/utils";
import ControlledTable from "../../../components/controlled-table/controlled-table";
import { IColumn, ISortedBy, ITableRowData } from "../../../components/data-table/data-table";
import { noop } from "../../../utils";
import DocumentModal from "./document-modal";

const documentsColumns: IColumn[] = [{
  key: "file_name",
  displayName: "File",
  dataType: {
    type: "text"
  }
}, {
  key: "file_type",
  displayName: "Type",
  dataType: {
    type: "text"
  }
},
{
  key: "date",
  displayName: "Uploaded date",
  dataType: {
    type: "dateTime"
  }
}];

interface IDocumentProps {
  alertcaseId: string;
}

const Documents = ({alertcaseId}: IDocumentProps) => {
  const authCtx = useContext(AuthContext);

  const [showModal, setShowModal] = useState(false);
  const [data, setData] = useState<ITableRowData[]>([]);
  const [sortedByCol, setSortedByCol] = useState<ISortedBy>({column: "date", direction: "desc"});

  const [isUserReadOnly, setIsUserReadOnly] = useState(false);

  // New Document
  const { sendRequest: postDocument, status: postDocumentStatus, data: postDocumentData, error: postDocumentError } = useHttp(postSingleNonTableImport, false, authCtx.token, "default", "manager");
  // Get all documents
  const { sendRequest: getTableData, status: getTableDataStatus, data: getTableDataResult, error: getTableDataError } = useHttp(getAllData, true, authCtx.token, "document", "manager");
  // Delete documents
  const { sendRequest: deleteMultipleRequest, status: deleteMultipleStatus, data: deleteMultipleData, error: deleteMultipleError } = useHttp(deleteMultiple, false, authCtx.token, "document", "manager");
  // Download document
  const { sendRequest: getDownload, status: getDownloadStatus, data: getDownloadData, error: getDownloadError } = useHttpNonTable(downloadFile, false, authCtx.token, "document", "manager");

  useEffect(() => {
    if (!alertcaseId) {
      return;
    }
    getTableData(`alertcase/${alertcaseId}/documents?order_by=${sortedByCol.column} ${sortedByCol.direction}`);
  }, [sortedByCol, alertcaseId]);

  useEffect(() => {
    const roles = authCtx.selectedTenant.roles || [];
    setIsUserReadOnly(roles.length === 1 && roles[0] === "Read");
  }, [authCtx.selectedTenant]);

  useEffect(() => {
    if (getTableDataStatus === "completed" && !getTableDataError && getTableDataResult) {
      setData(getTableDataResult);
    }
  }, [getTableDataStatus, getTableDataError, getTableDataResult]);

  useEffect(() => {
    if (postDocumentStatus === "completed" && !postDocumentError && postDocumentData) {
      setShowModal(false);
      setData(postDocumentData);
    }
  }, [postDocumentStatus, postDocumentError, postDocumentData]);

  useEffect(() => {
    if (deleteMultipleStatus === "completed" && !deleteMultipleError && deleteMultipleData) {
      setData(deleteMultipleData);
    }
  }, [deleteMultipleStatus, deleteMultipleError, deleteMultipleData]);

  const handleOnDelete = (ids: string[]) => {
    deleteMultipleRequest(`alertcase/${alertcaseId}/documents?order_by=${sortedByCol.column} ${sortedByCol.direction}`, {ids: ids});
  };

  const handleModalSubmit = (files: File[]) => {
    setShowModal(false);
    if (files.length === 0) {
      return;
    }
    const formData = new FormData();
    files.forEach(file => formData.append("files", file));

    postDocument(`alertcase/${alertcaseId}/documents?order_by=${sortedByCol.column} ${sortedByCol.direction}`, formData, "form");
  };

  const handleOnRowClick = (id: string) => {
    if (isUserReadOnly) {
      return;
    }
    getDownload(`document/${id}`);
  };

  useEffect(() => {
    if (getDownloadStatus === "completed" && !getDownloadError && getDownloadData) {
      // Create an url manually and click on that to trigger the download.
      const url = window.URL.createObjectURL(getDownloadData.file);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", getDownloadData.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    }
  }, [getDownloadStatus]);

  return <div className="documents">
    {validateLoadingStatusNew([postDocumentStatus, getTableDataStatus,deleteMultipleStatus, getDownloadStatus]) && alertcaseId && <Spinner />}
    <DocumentModal showModal={showModal} onModalSubmit={handleModalSubmit} onModalClosed={() => setShowModal(false)} />
    <ControlledTable readOnly={isUserReadOnly} data={data} onDeleted={handleOnDelete} onSorted={setSortedByCol} onNewClicked={() => setShowModal(true)} onRowClick={handleOnRowClick} onScroll={noop} selectionMode="multi" columns={documentsColumns} />
  </div>;
};

export default Documents;